/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { FileOperation } from '../models/file-operation';
import { environment } from '../../../../environments/environment';
import { AzureFileResponse } from '../models/azure-file-response';
import { PaginatedResponse } from '../../../shared/interfaces/pagination';
import { FileDetails, SharingInfo } from '../models/file-details';
import { SharedConnectionUsers } from '../../../shared/models/shared-connection-users';
import { FileShareDetails } from '../models/file-share-details';
import { BlockBlobClient } from '@azure/storage-blob';
import { BehaviorSubject, Subject } from 'rxjs';
import { UploadItem } from '../../../shared/models/upload-item';
import { FileDownloadResponse } from '../models/file-download-response';

@Injectable({
  providedIn: 'root',
})
export class FileUploadService {
  private http: HttpClient = inject(HttpClient);

  private apiBaseUrl = environment.apiBaseUrl;

  private uploads: UploadItem[] = [];
  public uploads$ = new BehaviorSubject<UploadItem[]>([]);
  private uploadControllers = new Map<string, AbortController>();

  // Event emitter for upload cancellation
  private uploadCancelledSource = new Subject<void>();
  public uploadCancelled$ = this.uploadCancelledSource.asObservable();

  // Generate a unique ID for uploads
  private generateId(): string {
    return Math.random().toString(36).substring(2, 15);
  }

  private updateUploadState(id: string, changes: Partial<UploadItem>): void {
    const index = this.uploads.findIndex((u) => u.id === id);
    if (index !== -1) {
      this.uploads[index] = { ...this.uploads[index], ...changes };
      this.uploads$.next([...this.uploads]);
    }
  }

  cancelUpload(uploadId: string): void {
    const uploadItem = this.uploads.find((u) => u.id === uploadId);
    if (!uploadItem) return;

    // Abort the upload
    const controller = this.uploadControllers.get(uploadId);
    if (controller) {
      controller.abort();
      this.uploadControllers.delete(uploadId);
    }

    // Call delete API if fileId exists
    if (uploadItem.fileId) {
      this.deleteFile(uploadItem.fileId).subscribe({
        next: () => {
          console.log(`File ${uploadItem.fileId} deleted successfully`);
          // Emit event to refresh file listing
          this.uploadCancelledSource.next();
        },
        error: (err) => {
          console.error(`Failed to delete file ${uploadItem.fileId}:`, err);
          // Still emit event to refresh file listing even if delete fails
          this.uploadCancelledSource.next();
        },
      });
    }

    // Remove from uploads list to clear from UI
    this.uploads = this.uploads.filter((u) => u.id !== uploadId);
    this.uploads$.next([...this.uploads]);
  }

  get activeUploadsCount(): number {
    return this.uploads.filter((u) => u.status === 'uploading').length;
  }

  uploadFile(file: File, operations: number[]): Observable<AzureFileResponse> {
    const formData = new FormData();
    formData.append('file_name', file.name);
    formData.append('operation', JSON.stringify({ operations }));

    const apiRes$ = this.http.post<AzureFileResponse>(
      `${this.apiBaseUrl}/api/azure-storage/file/upload`,
      formData,
    );

    // Return immediately after getting SAS URL, start background upload
    return apiRes$.pipe(
      map((res) => {
        // Create upload item
        const uploadId = this.generateId();
        const uploadItem: UploadItem = {
          id: uploadId,
          file: file,
          fileName: file.name,
          fileId: res.file_id,
          progress: 0,
          status: 'uploading',
        };
        this.uploads.push(uploadItem);
        this.uploads$.next([...this.uploads]);

        // Start background upload to Azure Blob Storage
        this.uploadWithSDK(res.url, file, uploadId);
        return res;
      }),
    );
  }

  uploadSMEFiles(fileNames: string[]): Observable<AzureFileResponse[]> {
    let headers = new HttpHeaders();
    headers = headers.set('X-Skip-Loader', 'true');
    return this.http.post<AzureFileResponse[]>(
      `${this.apiBaseUrl}/api/sme/file/upload`,
      { file_names: fileNames },
      { headers },
    );
  }

  public startSMEUpload(res: AzureFileResponse, file: File): string {
    const uploadId = this.generateId();
    const uploadItem: UploadItem = {
      id: uploadId,
      file: file,
      fileName: file.name,
      fileId: res.file_id,
      progress: 0,
      status: 'uploading',
    };
    this.uploads.push(uploadItem);
    this.uploads$.next([...this.uploads]);

    this.uploadWithSDK(res.url, file, uploadId);
    return uploadId;
  }

  public async uploadWithSDK(sasUrl: string, file: File, uploadId: string) {
    // sasUrl example:
    // https://<account>.blob.core.windows.net/<container>/<blobname>?sv=...&sig=...

    const blockBlobClient = new BlockBlobClient(sasUrl);
    const abortController = new AbortController();
    this.uploadControllers.set(uploadId, abortController);

    try {
      await blockBlobClient.uploadBrowserData(file, {
        blockSize: 4 * 1024 * 1024, // 4MB block size
        maxSingleShotSize: 4 * 1024 * 1024, // Force chunking for files > 4MB
        concurrency: 5,
        abortSignal: abortController.signal,
        onProgress: (ev: any) => {
          const percent = Math.round((ev.loadedBytes / file.size) * 100);
          this.updateUploadState(uploadId, { progress: percent });
        },
      });
      this.uploadControllers.delete(uploadId);
      this.updateUploadState(uploadId, {
        status: 'completed',
        progress: 100,
        file: null as any, // Nullify file reference to allow GC
      });

      // Automatically remove from list after 5 seconds to keep memory clean
      setTimeout(() => {
        this.uploads = this.uploads.filter((u) => u.id !== uploadId);
        this.uploads$.next([...this.uploads]);
      }, 5000);
    } catch (error: any) {
      this.uploadControllers.delete(uploadId);
      if (error.name === 'AbortError') {
        // Upload was cancelled, state already updated in cancelUpload
        return;
      }
      console.error('Upload failed:', error);
      this.updateUploadState(uploadId, {
        status: 'failed',
        error: 'Upload failed',
        file: null as any, // Nullify file reference even on failure
      });

      // Keep failed uploads for a bit longer so user can see error, then clear
      setTimeout(() => {
        this.uploads = this.uploads.filter((u) => u.id !== uploadId);
        this.uploads$.next([...this.uploads]);
      }, 10000);
    }
  }

  getAllUploadedFiles(
    pageNo = 1,
    pageSize = 10,
    skipLoader = false,
    filterBy?: string,
    searchQuery?: string,
    uploaded_connection?: string,
    uploaded_user?: string,
    upload_status?: string,
    shared_with_me?: boolean,
    pdm_only?: boolean,
    pdm_status?: string | number,
  ): Observable<PaginatedResponse<FileDetails>> {
    let params = new HttpParams();
    params = params.set('page', pageNo.toString());
    params = params.set('page_size', pageSize.toString());
    if (filterBy) params = params.set('operation_type', filterBy.toString());
    if (searchQuery) params = params.set('search', searchQuery);
    if (uploaded_connection)
      params = params.set('uploaded_connection', uploaded_connection);
    if (uploaded_user) params = params.set('uploaded_user', uploaded_user);
    if (upload_status) params = params.set('upload_status', upload_status);
    if (shared_with_me !== undefined)
      params = params.set('shared_with_me', shared_with_me.toString());
    if (pdm_only !== undefined)
      params = params.set('pdm_only', pdm_only.toString());
    if (pdm_status)
      params = params.set('pdm_sharing_status', pdm_status.toString());

    return this.http.get<PaginatedResponse<FileDetails>>(
      `${this.apiBaseUrl}/api/azure-storage/upload/files`,
      {
        params,
        headers: {
          'X-Skip-Loader': skipLoader ? 'true' : 'false',
        },
      },
    );
  }

  downloadFile(
    fileId: number,
    inline = false,
  ): Observable<FileDownloadResponse> {
    const url = `${this.apiBaseUrl}/api/azure-storage/download/${fileId}${inline ? '?inline=true' : ''}`;
    return this.http.get<FileDownloadResponse>(url);
  }

  deleteFile(fileId: number): Observable<HttpResponse<object>> {
    return this.http.delete<HttpResponse<object>>(
      `${this.apiBaseUrl}/api/azure-storage/delete/${fileId}`,
    );
  }

  getFileOperations(): Observable<FileOperation[]> {
    return this.http.get<FileOperation[]>(
      `${this.apiBaseUrl}/api/file/get-operations`,
    );
  }

  getUsersForSharing(
    fileId: number,
    searchTerm: string,
    page = 1,
    pageSize = 10,
  ): Observable<PaginatedResponse<SharedConnectionUsers>> {
    let params = new HttpParams();
    if (searchTerm) {
      params = params.set('search_key', searchTerm);
    }
    params = params.set('page', page.toString());
    params = params.set('page_size', pageSize.toString());

    return this.http.get<PaginatedResponse<SharedConnectionUsers>>(
      `${this.apiBaseUrl}/api/file/${fileId}/share/users`,
      {
        params,
        headers: {
          'X-Skip-Loader': 'true',
        },
      },
    );
  }

  searchUsersByEmail(
    fileId: number,
    email: string,
  ): Observable<PaginatedResponse<SharedConnectionUsers>> {
    let params = new HttpParams();
    params = params.set('email', email);

    return this.http.get<PaginatedResponse<SharedConnectionUsers>>(
      `${this.apiBaseUrl}/api/file-share/${fileId}/users`,
      {
        params,
        headers: {
          'X-Skip-Loader': 'true',
        },
      },
    );
  }

  getFileSharedDetails(fileId: number): Observable<FileShareDetails> {
    return this.http
      .get<FileShareDetails>(`${this.apiBaseUrl}/api/file/${fileId}/shared`)
      .pipe(
        map((response: any) => {
          return {
            ...response,
            users: response.users.map((user: any) => ({
              ...user,
              id: user.id || user.user_id, // Ensure id is populated from user_id if needed
            })),
          };
        }),
      );
  }

  shareFile(
    fileId: number,
    shareConnections: number[],
    shareUsers: number[],
    unshareConnections: number[] = [],
    unshareUsers: number[] = [],
  ): Observable<void> {
    const shareData = {
      share: {
        connection_ids: shareConnections,
        user_ids: shareUsers,
      },
      unshare: {
        connection_ids: unshareConnections,
        user_ids: unshareUsers,
      },
    };
    return this.http.put<void>(
      `${this.apiBaseUrl}/api/file/${fileId}/share`,
      shareData,
    );
  }

  getOperationsByConnectionNames(
    connectionNames: string[],
    skipLoader = false,
  ): Observable<{ connection_name: string; operations: FileOperation[] }[]> {
    return this.http.post<
      { connection_name: string; operations: FileOperation[] }[]
    >(
      `${this.apiBaseUrl}/api/file/operations-by-connections`,
      {
        connection_names: connectionNames,
      },
      {
        headers: {
          'X-Skip-Loader': skipLoader ? 'true' : 'false',
        },
      },
    );
  }

  retriggerValidation(fileId: number, operations: number[]): Observable<void> {
    return this.http.post<void>(
      `${this.apiBaseUrl}/api/pdm/file/retrigger-validation`,
      {
        file_id: fileId,
        operations,
      },
    );
  }

  submitFileApproval(fileId: number, action: number): Observable<void> {
    return this.http.post<void>(
      `${this.apiBaseUrl}/api/pdm/file/${fileId}/approval`,
      {
        action,
      },
    );
  }

  getPdmPendingCount(): Observable<{ count: number }> {
    return this.http.get<{ count: number }>(
      `${this.apiBaseUrl}/api/pdm/file/pending/count`,
    );
  }

  getPdmFileSharingInfo(fileId: number): Observable<SharingInfo> {
    return this.http.get<SharingInfo>(
      `${this.apiBaseUrl}/api/pdm/file/${fileId}/sharing-info`,
    );
  }
}
